import type { Plugin, Context } from '@segment/analytics-next';

import { LABELS } from '@app/config';
import { DOM, ironBuildVersionIsV3 } from '@app/utils';
import set from 'lodash/set';
import get from 'lodash/get';
import getOrSetStockXSessionId from '@app/user/utils/getOrSetStockXSessionId';

const EXPERIMENTS_PATH = ironBuildVersionIsV3
  ? '__STOCKX_EXPERIMENTS__'
  : '__NEXT_DATA__.props.pageProps.req.appContext.states.experiments.value';

const PAGE_CONFIG_PATH = '__STOCKX_PAGE_CONFIG__';

const enrichSegmentEvent = (ctx: Context) => {
  //
  // Set common context values
  set(ctx, 'event.context.app.name', LABELS.APP_HUMAN_NAME);
  set(ctx, 'event.context.app.version', LABELS.APP_HUMAN_VERSION);
  set(ctx, 'event.context.device.id', DOM.getCookie('stockx_device_id'));
  set(ctx, 'event.context.sessionId', getOrSetStockXSessionId());

  //
  // set customer traits for all tracking events
  if (
    ['page', 'track'].includes(ctx.event.type) &&
    !ctx.event?.context?.traits
  ) {
    set(
      ctx,
      'event.context.traits',
      window.__STOCKX_USER_CONFIG__?.segmentCustomerTraits ?? {},
    );
  }

  if (
    ['page', 'track'].includes(ctx.event.type) &&
    !ctx.event?.context?.location
  ) {
    set(
      ctx,
      'event.context.location',
      window.__STOCKX_USER_CONFIG__?.segmentGeoLocation ?? {},
    );
  }

  if (
    ['page', 'track'].includes(ctx.event.type) &&
    !ctx.event?.context?.variants
  ) {
    set(
      ctx,
      'event.context.variants',
      get(window, `${EXPERIMENTS_PATH}.abVariants`, []),
    );
  }

  //
  // set screenName from global stockx page config.
  if (
    ['page', 'track'].includes(ctx.event.type) &&
    !ctx?.event?.properties?.screenName
  ) {
    set(
      ctx,
      'event.properties.screenName',
      get(window, `${PAGE_CONFIG_PATH}.tracking.segmentScreenName`, 'Other'),
    );
  }

  //
  // Provide our own language to fix:
  // https://bugs.webkit.org/show_bug.cgi?id=163096
  // https://bugs.webkit.org/show_bug.cgi?id=225461
  // https://github.com/segmentio/analytics-next/blob/2691b8808e3e33f97ca7a79e041c228f6fe3a67d/src/plugins/segmentio/normalize.ts#L129-L134
  const locale = navigator.language;
  const [language, dialect] = locale.split('-');
  set(ctx, 'event.context.locale', `${language}-${dialect.toUpperCase()}`);

  return ctx;
};

/**
 *
 */
const globalEnrichmentPlugin: Plugin = {
  name: 'globalEnrichmentPlugin',
  type: 'enrichment',
  version: '1.0.0',

  isLoaded: () => true,
  load: () => Promise.resolve(),

  track: enrichSegmentEvent,
  identify: enrichSegmentEvent,
  page: enrichSegmentEvent,
  alias: enrichSegmentEvent,
  group: enrichSegmentEvent,
  screen: enrichSegmentEvent,
};

export default globalEnrichmentPlugin;
